import React, { useEffect, useState } from 'react';

import { Button } from '../../components/Button/Button';
import { CarouselArrow } from '../../components/CarouselArrow/CarouselArrow';
import { SportIcon } from '../../components/SportIcon/SportIcon';
import { useCarousel } from '../../hooks/useCarousel';
import { useInView } from '../../hooks/useInView';
import type { Slide } from './slides';

import './Carousel.css';

type CarouselProps = {
    slides: Slide[];
    nav?: React.ReactNode;
    autoplayMs?: number;
};

const DEFAULT_AUTOPLAY_MS = 6000;

export function Carousel({ slides, nav, autoplayMs = DEFAULT_AUTOPLAY_MS }: CarouselProps) {
    const total = slides.length;
    const [sectionRef, visible] = useInView<HTMLElement>({ once: false });
    const { index, goPrev, goNext } = useCarousel({
        total,
        autoplayMs: visible ? autoplayMs : undefined,
        enableKeyboard: true,
        keyboardScopeRef: sectionRef,
    });
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const id = window.requestAnimationFrame(() => setMounted(true));
        return () => window.cancelAnimationFrame(id);
    }, []);

    if (total === 0) { return null; }

    const current = slides[index];

    return (
        <section ref={sectionRef} className="fa-carousel" aria-roledescription="carousel">
            <div className={`fa-carousel__viewport${mounted ? ' fa-carousel__viewport--ready' : ''}`}>
                {slides.map((slide, i) => (
                    <img
                        key={slide.id}
                        className={`fa-card__bg${i === index ? ' fa-card__bg--active' : ''}`}
                        src={slide.backgroundImage}
                        alt=""
                        aria-hidden="true"
                    />
                ))}
                <div className="fa-card__overlay" aria-hidden="true" />
                <article className="fa-card">
                    <div className="fa-card__copy">
                        <div className="fa-card__eyebrow-row">
                            <div className="fa-card__eyebrow" key={current.id}>
                                <img
                                    className="fa-card__avatar"
                                    src={current.avatarImage}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <span className="fa-card__name">{current.name}</span>
                                <span className="fa-card__sport-icon">
                                    {current.sportIconUrl
                                        ? <img src={current.sportIconUrl} alt="" loading="lazy" decoding="async" />
                                        : <SportIcon iconKey={current.sportIcon} />}
                                </span>
                            </div>
                            <div className="fa-carousel__controls">
                                <CarouselArrow
                                    direction="prev"
                                    ariaLabel="Previous slide"
                                    onClick={goPrev}
                                    size="compact"
                                    focusColor="var(--faves-text)"
                                />
                                <CarouselArrow
                                    direction="next"
                                    ariaLabel="Next slide"
                                    onClick={goNext}
                                    size="compact"
                                    focusColor="var(--faves-text)"
                                />
                            </div>
                        </div>
                        <h1 className="fa-card__heading faves-display">{current.heading}</h1>
                        <p className="fa-card__subhead faves-eyebrow--uppercase">{current.subhead}</p>
                    </div>
                    <Button
                        label={current.cta.label}
                        href={current.cta.href}
                        variant="accent"
                        className="fa-card__cta"
                    />
                    <div className="fa-card__stats">
                        {current.stats.map((stat, i) => (
                            <div
                                key={i}
                                className="fa-card__stat"
                                style={{ transitionDelay: `${480 + i * 140}ms` }}
                            >
                                <div className="fa-card__stat-value">{stat.value}</div>
                                <div className="fa-card__stat-label faves-eyebrow">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </article>
                {nav && <div className="fa-carousel__nav">{nav}</div>}
            </div>
        </section>
    );
}
